import React, { useCallback, useEffect, useState } from "react";
import { api, rupiah, downloadReport } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty, StatCard } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollText, FileSpreadsheet, FileText, ShoppingBag, Wallet, CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 50;
const fmtDate = (iso) => new Date(iso).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" });

const CATEGORY_STYLE = {
  Santri: "bg-emerald-100 text-emerald-700",
  Pendidik: "bg-violet-100 text-violet-700",
  ASC: "bg-amber-100 text-amber-700",
};

export default function Sales() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [sales, setSales] = useState([]);
  const [total, setTotal] = useState(0);
  const [todayTotal, setTodayTotal] = useState(0);
  const [fUnit, setFUnit] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);

  const params = useCallback(() => {
    const p = {};
    if (isAdmin && fUnit !== "all") p.unit = fUnit;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [isAdmin, fUnit, dateFrom, dateTo]);

  const load = useCallback(() => {
    api.get("/admin/reports/sales", { params: params() })
      .then((r) => { setSales(r.data.rows); setTotal(r.data.total); setTodayTotal(r.data.today_total || 0); setPage(1); })
      .catch(() => {});
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const rangeReady = dateFrom && dateTo;

  const doExport = async (format) => {
    if (!rangeReady) return toast.error("Pilih rentang tanggal (dari & sampai) terlebih dahulu untuk export");
    try {
      await downloadReport("/admin/reports/sales/export", { ...params(), format },
        format === "pdf" ? "laporan_penjualan.pdf" : "laporan_penjualan.xlsx");
    } catch (e) {
      toast.error("Gagal mengunduh laporan");
    }
  };

  const totalPages = Math.max(1, Math.ceil(sales.length / PAGE_SIZE));
  const pageRows = sales.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div>
      <PageHeader
        title="Laporan Penjualan"
        subtitle={isAdmin
          ? "Rekap transaksi Kopontren dalam bentuk tabel. Pilih rentang tanggal untuk meng-export ke Excel/PDF."
          : "Riwayat transaksi yang Anda tangani — gabungan Santri, Pendidik, dan Kartu ASC. Pilih rentang tanggal untuk export."}
        action={
          <div className="flex gap-2 no-print">
            <Button onClick={() => doExport("xlsx")} disabled={!rangeReady} data-testid="export-sales-xlsx" variant="outline" className="rounded-xl disabled:opacity-50">
              <FileSpreadsheet size={18} className="mr-2 text-emerald-600" /> Excel
            </Button>
            <Button onClick={() => doExport("pdf")} disabled={!rangeReady} data-testid="export-sales-pdf" variant="outline" className="rounded-xl disabled:opacity-50">
              <FileText size={18} className="mr-2 text-rose-600" /> PDF
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-end gap-3 mb-5">
        {isAdmin && (
          <Select value={fUnit} onValueChange={setFUnit}>
            <SelectTrigger data-testid="sales-filter-unit" className="w-40 rounded-xl bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Unit</SelectItem>
              <SelectItem value="putra">Putra</SelectItem>
              <SelectItem value="putri">Putri</SelectItem>
            </SelectContent>
          </Select>
        )}
        <div>
          <Label className="text-xs text-stone-400">Dari Tanggal</Label>
          <Input type="date" data-testid="sales-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="rounded-xl bg-white" />
        </div>
        <div>
          <Label className="text-xs text-stone-400">Sampai Tanggal</Label>
          <Input type="date" data-testid="sales-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="rounded-xl bg-white" />
        </div>
      </div>

      {!rangeReady && (
        <p className="text-xs text-amber-600 mb-4" data-testid="export-hint">* Pilih rentang tanggal terlebih dahulu untuk mengaktifkan tombol export Excel/PDF.</p>
      )}

      <div className={`grid ${isAdmin ? "grid-cols-2 max-w-md" : "grid-cols-1 sm:grid-cols-3 max-w-2xl"} gap-4 mb-6`}>
        <StatCard icon={ShoppingBag} label="Transaksi" value={sales.length} />
        <StatCard icon={Wallet} label="Total Penjualan" value={rupiah(total)} accent="emerald" />
        {!isAdmin && <StatCard icon={CalendarDays} label="Pendapatan Hari Ini" value={rupiah(todayTotal)} accent="amber" />}
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-x-auto">
        {sales.length === 0 ? (
          <Empty><ScrollText className="mx-auto mb-2" /> Belum ada transaksi pada rentang ini.</Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead className="w-10">#</TableHead>
                <TableHead>Tanggal</TableHead>
                {!isAdmin && <TableHead>Golongan</TableHead>}
                <TableHead>{isAdmin ? "Santri" : "Pembeli"}</TableHead>
                {isAdmin && <TableHead>Unit</TableHead>}
                <TableHead>Petugas</TableHead>
                <TableHead>Barang</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageRows.map((s, i) => (
                <TableRow key={s.id} data-testid={`sale-row-${s.id}`}>
                  <TableCell className="text-stone-400 text-sm">{(page - 1) * PAGE_SIZE + i + 1}</TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{fmtDate(s.created_at)}</TableCell>
                  {!isAdmin && (
                    <TableCell>
                      <span data-testid={`sale-category-${s.id}`} className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_STYLE[s.category] || "bg-stone-100 text-stone-600"}`}>{s.category}</span>
                    </TableCell>
                  )}
                  <TableCell className="font-medium text-stone-800">{s.student_name}</TableCell>
                  {isAdmin && (
                    <TableCell>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${s.unit === "putra" ? "bg-blue-100 text-blue-700" : "bg-pink-100 text-pink-700"}`}>{s.unit}</span>
                    </TableCell>
                  )}
                  <TableCell className="text-sm text-stone-500">{s.cashier_name}</TableCell>
                  <TableCell className="text-sm text-stone-600">{s.items_text || (s.items || []).map((it) => `${it.name} ×${it.qty}`).join(", ")}</TableCell>
                  <TableCell className="text-right font-mono font-semibold text-emerald-600 whitespace-nowrap">{rupiah(s.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {sales.length > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-stone-400" data-testid="sales-page-info">
            Menampilkan {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sales.length)} dari {sales.length} transaksi
          </p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="sales-prev-page" className="rounded-lg">
              <ChevronLeft size={16} />
            </Button>
            <span className="text-sm font-medium text-stone-600">Hal {page} / {totalPages}</span>
            <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="sales-next-page" className="rounded-lg">
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
